import { Food, FoodMeasure, FoodStatus } from '@cf-shared/data-model/food';
import { IngredientReal, IngredientType } from '@cf-shared/data-model/ingredient';
import { isRecipe, calculateRecipeTotalWeight } from '@cf-shared/data-model/recipe';

/**
 * The ingredient a food starts as when the user adds it to a meal: the amount and unit the
 * portion screen opens on.
 *
 * A recipe starts at its whole weight, any other food at its first serving (or 100 g when it has
 * none). The amount and unit the user chose for this food last time win over both. Shared by the
 * meal page's ingredient list and the meal photo scanner's "Add Food" row (#6807), so a food
 * opens on the same amount whichever way it is added.
 *
 * @param status the food's status, which the caller looks up: this function reads nothing itself.
 */
export function defaultIngredientForFood(food: Food, status: FoodStatus): IngredientReal {
  const ingredient: IngredientReal = {
    type: IngredientType.Real,
    foodId: food._id,
    name: food.name,
    weight_g: 100,
    weight_unit: undefined,
    status,
  };

  if (isRecipe(food)) {
    // getFoodByIdAsync auto-fixes totalWeight_g, but use utility as fallback
    ingredient.weight_g = food.totalWeight_g || calculateRecipeTotalWeight(food);
  } else {
    let measure: FoodMeasure;
    // make sure to handle eventual damaged data in the database!
    if (food.measures && typeof food.measures === 'object') {
      measure = food.measures[0];
      if (measure) {
        ingredient.weight_g = measure.weight_g || ingredient.weight_g;
        ingredient.custom_unit = measure.name;
      }
    }
  }

  // apply cached measure for all foods (including recipes)
  if (food.local.lastUsedMeasureUnitName) {
    ingredient.custom_unit = food.local.lastUsedMeasureUnitName;
  }
  if (food.local.lastUserMeasureWeight_g) {
    ingredient.weight_g = food.local.lastUserMeasureWeight_g;
  }
  if (food.local.lastUsedWeightUnit) {
    ingredient.weight_unit = food.local.lastUsedWeightUnit;
  }
  return ingredient;
}
